import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, Camera, ShieldCheck, UserCheck, FileCheck, ArrowLeft } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';

const CheckItem = ({ label, passed }) => (
  <div className="flex items-center gap-2">
    {passed ? (
      <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
    ) : (
      <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
    )}
    <span className={`text-sm ${passed ? 'text-emerald-700' : 'text-red-700'}`}>{label}</span>
  </div>
);

export default function AttendanceConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { result, photoPreview, staffName } = location.state || {};

  if (!result) {
    return (
      <DashboardLayout>
        <div className="max-w-lg mx-auto text-center py-20">
          <p className="text-ga-text-muted mb-4">No attendance submission data found.</p>
          <Link to="/dashboard/selfie-attendance" className="text-teal-600 hover:underline">
            Go to Selfie Attendance
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const isSuccess = result.success;
  const checks = result.checks || {};
  const type = result.type;
  const time = result.time;

  const formattedTime = time
    ? new Date(time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';
  const formattedDate = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto space-y-6">
        <button
          onClick={() => navigate('/dashboard/selfie-attendance')}
          className="flex items-center gap-1 text-sm text-ga-text-muted hover:text-ga-text-primary transition"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className={`ga-card rounded-xl shadow-sm border-2 p-6 space-y-5 ${
          isSuccess ? 'border-emerald-200' : 'border-red-200'
        }`}>
          {/* Header */}
          <div className="text-center">
            {isSuccess ? (
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-3">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-3">
                <XCircle className="h-9 w-9 text-red-600" />
              </div>
            )}
            <h2 className="text-xl font-bold text-ga-text-primary">
              {isSuccess
                ? type === 'in' ? 'Clocked In Successfully' : 'Clocked Out Successfully'
                : 'Verification Failed'}
            </h2>
            {staffName && (
              <p className="text-ga-text-muted mt-1">{staffName}</p>
            )}
          </div>

          {/* Photo thumbnail */}
          {photoPreview && (
            <div className="flex justify-center">
              <img
                src={photoPreview}
                alt="Submitted selfie"
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
              />
            </div>
          )}

          {/* Time info */}
          {isSuccess && (
            <div className="bg-gray-50 rounded-lg p-4 text-center space-y-1">
              <div className="flex items-center justify-center gap-2 text-ga-text-muted text-sm">
                <Clock className="h-4 w-4" />
                {type === 'in' ? 'IN-TIME' : 'OUT-TIME'} (from EXIF)
              </div>
              <p className="text-2xl font-bold text-ga-text-primary">{formattedTime}</p>
              <p className="text-xs text-ga-text-muted">{formattedDate}</p>
            </div>
          )}

          {/* Verification checks */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-ga-text-primary flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-teal-600" />
              Verification Status
            </h3>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <CheckItem label="EXIF metadata valid" passed={checks.exif} />
              <CheckItem label="Image authentic (not manipulated)" passed={checks.authenticity} />
              <CheckItem label="Face detected" passed={checks.face_detected} />
              <CheckItem label="Face matched with records" passed={checks.face_matched} />
              <CheckItem label="Within facility geofence (200m)" passed={checks.geofence} />
            </div>
          </div>

          {/* Rejection reasons */}
          {!isSuccess && result.rejection_reasons?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-1">
              <p className="text-sm font-medium text-red-800">Rejection reasons:</p>
              <ul className="list-disc pl-5 space-y-1">
                {result.rejection_reasons.map((r, i) => (
                  <li key={i} className="text-sm text-red-700">{r}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Link
              to="/dashboard/selfie-attendance"
              className="flex-1 px-4 py-3 rounded-xl bg-teal-600 text-white font-semibold
                         hover:bg-teal-700 transition text-center shadow"
            >
              {isSuccess ? 'Done' : 'Try Again'}
            </Link>
            <Link
              to="/dashboard/selfie-attendance/history"
              className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-ga-text-primary
                         font-medium hover:bg-gray-50 transition text-center"
            >
              View History
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
